import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Field, Modal, Notice } from '@/components/Modal';
import { apiFetch } from '@/lib/api/client';

/**
 * 구글 OTP 등록·해제 (DOC-142 §5 · V605).
 *
 * <p>민감한 실행(NAS 관리자 계정 등) 직전에 서버가 6자리를 받는다. 그 열쇠를 본인이 여기서 단다 —
 * 남의 것을 대신 다는 길은 없다(`MeTotpController`).
 *
 * <p>쓰는 것은 6자리뿐이다(사용자 지시 2026-11-02). 등록은 비밀번호 → QR + 첫 6자리, 해제는 비밀번호 + 6자리.
 * QR 아래 비밀값 글자와 복구 코드는 걷었다.
 */

type TotpStatus = {
  enrolled: boolean;
  pending: boolean;
  enabledAt: string | null;
  lockedUntil: string | null;
  /** 로그인할 때 OTP(V618). 재시작 전 서버는 안 준다. */
  loginRequired?: boolean;
};

const STATUS_KEY = ['me', 'totp'] as const;

export function TotpRow() {
  const { data: status } = useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => apiFetch<TotpStatus>('/v1/me/totp'),
  });
  const [open, setOpen] = useState<'setup' | 'disable' | 'login' | null>(null);

  const enrolled = status?.enrolled ?? false;
  const loginRequired = status?.loginRequired ?? false;

  return (
    // 마이페이지 계정 표(`.my-info`)의 한 칸이다.
    <div>
      <dt>구글 OTP</dt>
      <dd className="flex items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
        <span style={{ fontSize: '12px', color: enrolled ? '#15803d' : 'var(--text-muted)' }}>
          {status ? (enrolled ? '등록됨' : '미등록') : ''}
        </span>
        {/* 등록 전이어도 세운다 — 누르면 서버가 「먼저 등록」 으로 답한다. */}
        <label
          className="flex items-center gap-1"
          style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          <input type="checkbox" checked={loginRequired} onChange={() => setOpen('login')} />
          로그인에 사용
        </label>
      </dd>
      <span className="my-info-action">
        {enrolled ? (
          <button className="btn-secondary" style={rowButton} onClick={() => setOpen('disable')}>해제</button>
        ) : (
          <button className="btn-secondary" style={rowButton} onClick={() => setOpen('setup')}>등록</button>
        )}
      </span>

      {open === 'setup' && <TotpSetupModal onClose={() => setOpen(null)} />}
      {open === 'disable' && <TotpDisableModal onClose={() => setOpen(null)} />}
      {open === 'login' && <TotpLoginSwitchModal next={!loginRequired} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** 로그인 OTP 켜기·끄기(V618) — 둘 다 6자리를 받는다. */
function TotpLoginSwitchModal({ next, onClose }: { next: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');

  const save = useMutation({
    mutationFn: () => apiFetch<void>('/v1/me/totp/login-required', { method: 'POST', body: { required: next, code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      onClose();
    },
  });

  return (
    <Modal
      title={next ? '로그인할 때 OTP 켜기' : '로그인할 때 OTP 끄기'}
      onClose={onClose}
      maxWidth="380px"
      actions={
        <button className="btn-primary" disabled={code.length !== 6 || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? '확인 중…' : next ? '켜기' : '끄기'}
        </button>
      }
    >
      <div className="space-y-3">
        <Field label="6자리">
          <CodeInput value={code} onChange={setCode} onEnter={() => code.length === 6 && save.mutate()} />
        </Field>
        {save.isError && <Notice tone="error">{save.error.message}</Notice>}
      </div>
    </Modal>
  );
}

function TotpSetupModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => apiFetch<{ otpauthUri: string }>('/v1/me/totp/setup', { method: 'POST', body: { password } }),
    onSuccess: (data) => setOtpauthUri(data.otpauthUri),
  });
  const enable = useMutation({
    mutationFn: () => apiFetch<void>('/v1/me/totp/enable', { method: 'POST', body: { code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      onClose();
    },
  });

  const header = otpauthUri ? (
    <button className="btn-primary" disabled={code.length !== 6 || enable.isPending} onClick={() => enable.mutate()}>
      {enable.isPending ? '확인 중…' : '등록'}
    </button>
  ) : (
    <button className="btn-primary" disabled={!password || start.isPending} onClick={() => start.mutate()}>
      {start.isPending ? '확인 중…' : '다음'}
    </button>
  );

  return (
    <Modal title="구글 OTP 등록" onClose={onClose} maxWidth="420px" actions={header}>
      <div className="space-y-3">
        {!otpauthUri && (
          <Field label="비밀번호">
            <input
              className="input-field"
              type="password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && password && start.mutate()}
            />
          </Field>
        )}
        {start.isError && <Notice tone="error">{start.error.message}</Notice>}

        {otpauthUri && (
          <>
            <OtpQr uri={otpauthUri} />
            <Field label="6자리">
              <CodeInput value={code} onChange={setCode} onEnter={() => code.length === 6 && enable.mutate()} />
            </Field>
            {enable.isError && <Notice tone="error">{enable.error.message}</Notice>}
          </>
        )}
      </div>
    </Modal>
  );
}

function TotpDisableModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const disable = useMutation({
    mutationFn: () => apiFetch<void>('/v1/me/totp/disable', { method: 'POST', body: { password, code } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      onClose();
    },
  });

  return (
    <Modal
      title="구글 OTP 해제"
      onClose={onClose}
      maxWidth="380px"
      actions={
        <button className="btn-primary" disabled={!password || code.length !== 6 || disable.isPending} onClick={() => disable.mutate()}>
          {disable.isPending ? '확인 중…' : '해제'}
        </button>
      }
    >
      <div className="space-y-3">
        <Field label="비밀번호">
          <input
            className="input-field"
            type="password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <Field label="6자리">
          <CodeInput value={code} onChange={setCode} onEnter={() => password && code.length === 6 && disable.mutate()} autoFocus={false} />
        </Field>
        {disable.isError && <Notice tone="error">{disable.error.message}</Notice>}
      </div>
    </Modal>
  );
}

function CodeInput({
  value,
  onChange,
  onEnter,
  autoFocus = true,
}: {
  value: string;
  onChange: (next: string) => void;
  onEnter: () => void;
  autoFocus?: boolean;
}) {
  return (
    <input
      className="input-field"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      autoFocus={autoFocus}
      value={value}
      onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
      onKeyDown={(event) => event.key === 'Enter' && onEnter()}
      style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '18px' }}
    />
  );
}

/** QR 은 브라우저에서 그린다 — 비밀값이 든 주소를 바깥 QR 서비스로 보내지 않는다(`QrCodeModal` 과 같은 이유). */
function OtpQr({ uri }: { uri: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(uri, { width: 400, margin: 1, errorCorrectionLevel: 'M' })
      .then((value) => {
        if (alive) setDataUrl(value);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [uri]);

  return dataUrl ? (
    <img src={dataUrl} alt="구글 OTP QR" style={{ width: '200px', height: '200px', margin: '0 auto', imageRendering: 'pixelated' }} />
  ) : null;
}

/** 정보 표 칸 안의 작은 버튼 — MyPage 의 cellButton 과 같은 값이다. */
const rowButton = { fontSize: '12px', padding: '3px 10px' } as const;
